import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { Menu, MenuGroup, MenuItem, MenuItemLabel, MenuItemTrailing } from '@plato/ui/primitives/menu';
import { Typography } from '@plato/ui/primitives/typography';
import { Fragment, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { AskPlatoConversation } from '../hooks/use-ask-plato-chat.ts';
import {
    conversationTimestampLabel,
    groupConversationsByRecency,
    type ConversationRecencyGroup,
} from '../utils/conversation-recency.ts';

export type AskPlatoHistoryProps = {
    /** Most recent first; `undefined` while loading, which renders nothing yet. */
    conversations: AskPlatoConversation[] | undefined;
    /** A row press resumes that conversation in the chat view. */
    onResume: (conversation: { conversationId: string; title: string | undefined; updatedAt: Date }) => void;
    /** Blocks row presses while a reply streams or another conversation is loading. */
    disabled?: boolean;
};

/**
 * The history view: past conversations as a `Menu`, grouped by recency with
 * the title left and the `updatedAt` timestamp right.
 */
export function AskPlatoHistory({ conversations, onResume, disabled }: AskPlatoHistoryProps): ReactElement {
    const { t } = useTranslation('ask-plato');
    const locale = useResolvedLocale();

    const groups = groupConversationsByRecency(conversations ?? [], new Date());

    const groupLabel = (group: ConversationRecencyGroup): string => {
        switch (group) {
            case 'today':
                return t('history_today');
            case 'yesterday':
                return t('history_yesterday');
            case 'last7days':
                return t('history_last_7_days');
            default:
                return t('history_older');
        }
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {conversations !== undefined && groups.length === 0 && (
                <Typography variant="body" className="px-relaxed py-default text-content-weaker">
                    {t('history_empty')}
                </Typography>
            )}
            {groups.length > 0 && (
                <Menu>
                    {groups.map(({ group, items }) => (
                        <MenuGroup key={group} label={groupLabel(group)}>
                            {items.map((conversation) => (
                                <Fragment key={conversation.id}>
                                    <MenuItem
                                        label={conversation.title ?? t('new_chat')}
                                        disabled={disabled}
                                        onClick={() => {
                                            onResume({
                                                conversationId: conversation.id,
                                                title: conversation.title,
                                                updatedAt: conversation.updatedAt,
                                            });
                                        }}
                                    >
                                        <MenuItemLabel />
                                        <MenuItemTrailing className="text-caption tabular-nums">
                                            {conversationTimestampLabel(
                                                conversation.updatedAt,
                                                new Date(),
                                                locale,
                                            )}
                                        </MenuItemTrailing>
                                    </MenuItem>
                                </Fragment>
                            ))}
                        </MenuGroup>
                    ))}
                </Menu>
            )}
        </div>
    );
}
